import { Howl, Howler } from "howler";

import "./howler.extension";
interface ExtendedHowl extends Howl {
    setCancelled: (cancelled: boolean) => void;
    playIn: (millis: number, scheduled: boolean, sprite?: string | number) => number;
}

/**
 * If an audio would be seeked to sometime between 0 and 10 seconds, it should start at 0 seconds, instead.
 * 
 * RATHER PLAY THE AUDIO TOO LATE, THAN CUTTING IT OFF!
 */
const SLACK_MILLIS = 10*1000;

/**
 * AudioScheduler:
 * A class that keeps track of a map of audio-url-paths to Howl objects.
 * The constructor takes a list of audio formats that are available.
 * It provides:
 * - a method "preload(url)" which creates a Howl([url + format1, url + format1, ...]) and stores it in the map (if not present)
 * - a method "playAt(url, timestamp)" which plays the audio at the given time (if present in the map, if not it calls preload(url) first). If timestamp is in the past, it plays immediately and seeks forward accordingly. If timestamp is in the future, it schedules the playback. If the audio needs time to load, it seeks forward accordingly. All other sounds are stopped.
 * - a method "play(url)" which plays the audio immediately, or after it being loaded (if present in the map, if not it calls preload(url) first). All other sounds are stopped.
 * - a method "mute(muted: boolean)" which mutes or unmutes the Howler object (i.e. all Howls)
 * - a method "stop()" which stops all Howls
 */
export class AudioScheduler {
    audioFormats: string[];
    audioMap: Map<string, ExtendedHowl>;
    onStatusChange: React.Dispatch<React.SetStateAction<string | null>>;

    preload: (url: string | string[]) => void;
    playAt: (url: string, timestamp: number) => void;
    play: (url: string, stopOtherSounds?: boolean) => void;
    stop: () => void;
    mute: (muted: boolean) => void;
    plannedPlaying: string | null = null;
    playing: string | null = null;

    private _silence: Howl;
    isPlayingSilence: boolean = false;
    playSilence: () => void;
    stopSilence: () => void;

    stopAllIncludingSilence: () => void;

    constructor(audioFormats: string[], onStatusChange: React.Dispatch<React.SetStateAction<string | null>>) {
        this.audioFormats = audioFormats;
        console.log("📣 AudioScheduler", audioFormats);
        this.onStatusChange = onStatusChange;
        this.audioMap = new Map();

        this._silence = new Howl({ src: ["/audio/silence.mp3"], loop: true,  });
        console.log("📣 preload SILENCE", this._silence);

        this.playSilence = () => {
            console.log("📣 play SILENCE", this._silence);
            this._silence.play();
            this.isPlayingSilence = true;
        }
        
        this.stopSilence = () => {
            console.log("📣 stop SILENCE", this._silence);
            this._silence.stop();
            this.isPlayingSilence = false;
        }

        this.preload = (urls) => {
            if (typeof urls === "string") urls = [urls];
            for (const url of urls) {
                if (!this.audioMap.has(url)) {
                    const urlAlreadyContainsFormat = this.audioFormats.some(format => url.toLowerCase().endsWith(format.toLowerCase()));
                    const src = urlAlreadyContainsFormat ? url : this.audioFormats.map(format => url + format);
                    // const loop = url.includes("silence");
                    const howl = new Howl({ src }) as ExtendedHowl;
                    console.log("📣 preload ", {url, howl});
                    howl.on("play", () => {
                        if (this.plannedPlaying === url) {
                            console.log("📣 playing", url, howl, this);
                            //if this.playing and the corresponding howl is actually playing: stop it (otherwise: cancel it)
                            if (this.playing && this.playing !== url) {
                                const playingHowl = this.audioMap.get(this.playing);
                                if (playingHowl) {
                                    if (playingHowl.playing()) {
                                        console.log("📣         - first stop playing sound:", this.playing, playingHowl);
                                        playingHowl.setCancelled(true);
                                        playingHowl.stop();
                                    } else {
                                        console.log("📣         - first cancel playing sound:", this.playing, playingHowl);
                                        playingHowl.setCancelled(true);
                                    }
                                }
                            }
                            onStatusChange(url);
                            this.playing = url;
                        } else {
                            //the sound was stopped in the meantime. Stop the playback immediately.
                            console.log("📣 sound started playing after being stopped. stop immediately", url, howl);
                            howl.setCancelled(true);
                            howl.stop();
                        }
                    });
                    howl.on("end", () => {
                        console.log("📣 ended", url);
                        if (this.playing === url) {
                            this.playing = null;
                        }
                        this.onStatusChange((oldStatus: string | null) => {
                            if (oldStatus === url) {
                                console.log("📣 ended and was the 'status'", url, howl);
                                return null;
                            } else {
                                console.log("📣 ended but was not the 'status'", url, howl, {oldStatus});
                                return oldStatus;
                            }
                        });
                    });
                    this.audioMap.set(url, howl);
                }
            }
        };
        this.playAt = (url, timestamp) => {
            // this.stop();
            this.preload(url);
            const howl = this.audioMap.get(url);
            if (!howl) return;
            this.plannedPlaying = url;
            const now = Date.now();
            //in case this howls is alreay scheduled, cancel it
            howl.setCancelled(true);
            //reset the cancelled flag
            howl.setCancelled(false);
            if (now > timestamp) {
                const seek = now-timestamp;
                const seekSeconds = seek / 1000;
                if (seek > SLACK_MILLIS) {
                    // play immediately, but seek forward
                    console.log("📣 playAt immediately (seek)", url, howl, {timestamp, now, seek, SLACK_MILLIS}, this);
                    if (seekSeconds > howl.duration()) {
                        if (howl.loop()) {
                            const shiftedSeekSeconds = seekSeconds % howl.duration();
                            console.log("📣     - duration=", howl.duration(), " => shiftedSeek: ", shiftedSeekSeconds);
                            howl.seek(shiftedSeekSeconds);
                        } else {
                            console.log("📣     - duration=", howl.duration(), " => don't play audio, because it's already finished");
                            return;
                        }
                    } else {
                        //seek fits into duration
                        howl.seek(seekSeconds);
                    }
                } else {
                    console.log("📣 playAt immediately (don't seek, slack is big enough)", url, howl, {timestamp, now, seek, SLACK_MILLIS}, this);
                    // play immediately
                    howl.seek(0);
                }
                howl.play();
            } else {
                // schedule playback
                console.log("📣 playAt scheduled", url, howl, {timestamp, now, delay: (timestamp-now)}, this);
                howl.playIn((timestamp - now), true, SLACK_MILLIS);
            }
        };
        this.play = (url, stopOtherSounds?: boolean) => {
            this.preload(url);
            const howl = this.audioMap.get(url);
            if (!howl) return;
            
            if (stopOtherSounds) {
                //stop all other Howls, except for the _silence Howl
                this.audioMap.forEach((otherHowl) => {
                    if (howl !== otherHowl) {
                        otherHowl.setCancelled(true);
                        otherHowl.stop();
                    }
                });
            }

            //in case this howls is alreay scheduled, cancel it
            howl.setCancelled(true);
            //reset the cancelled flag
            howl.setCancelled(false);
            
            console.log("📣 play (seek:0)", url, howl, this);
            
            //actually play the sound
            this.plannedPlaying = url;
            howl.seek(0);
            howl.play();
        };
        this.stop = () => {
            console.log("📣 stop", {currentSound: this.plannedPlaying}, this);
            try {
                this.audioMap.forEach((howl) => {
                    howl.setCancelled(true);
                    howl.stop();
                });
            } catch (e) {
                console.error("📣 error stopping", e);
            }
            this.plannedPlaying = null;
            onStatusChange(null);
        };
        this.stopAllIncludingSilence = () => {
            console.log("📣 stopAllIncludingSilence", {currentSound: this.plannedPlaying}, this);
            try {
                this.audioMap.forEach((howl) => {
                    howl.setCancelled(true);
                    howl.stop();
                });
                this._silence.stop();
                Howler.stop();
            } catch (e) {
                console.error("📣 error stopping", e);
            }
            this.plannedPlaying = null;
            onStatusChange(null);
        }
        this.mute = (muted) => {
            console.log("📣 mute", muted, this);
            Howler.mute(muted);
        };
    }
}
